/**
 * ActivitiesHub — Bigin-style tabbed page wrapping the four activity surfaces.
 *   Calendar · Tasks · Events · Calls
 * Each tab reuses an existing component; this is purely a composition layer
 * so there is ONE sidebar entry instead of three scattered across the nav.
 *
 * Tab state is reflected in `?tab=` so reps can deep-link to a specific view.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar as CalendarIcon, CheckSquare, Video, Phone } from 'lucide-react';
import { API } from '@/App';
import { format, parseISO, startOfDay, addDays } from 'date-fns';
import UnifiedCalendar from '@/pages/UnifiedCalendar';
import TasksHub from '@/pages/TasksHub';
import CallsTab from '@/pages/CallsTab';

const TABS = [
  { key: 'calendar', label: 'Calendar', icon: CalendarIcon },
  { key: 'tasks',    label: 'Tasks',    icon: CheckSquare },
  { key: 'events',   label: 'Events',   icon: Video },
  { key: 'calls',    label: 'Calls',    icon: Phone },
];

export default function ActivitiesHub() {
  const [params, setParams] = useSearchParams();
  const urlTab = params.get('tab');

  const initial = TABS.find(t => t.key === urlTab)?.key || 'calendar';
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    // Keep URL in sync with tab selection (without adding history entries)
    const next = new URLSearchParams(params);
    if (next.get('tab') !== tab) {
      next.set('tab', tab);
      setParams(next, { replace: true });
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="activities-hub">
      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 px-4 sm:px-6 bg-white dark:bg-slate-800" data-testid="activities-tabs">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={
                'relative px-4 py-3 text-sm font-medium inline-flex items-center gap-2 transition-colors ' +
                (active
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200')
              }
              data-testid={`activities-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {active && <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-emerald-500" />}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div className="flex-1 min-h-0 overflow-hidden overflow-y-auto">
        {tab === 'calendar' && <UnifiedCalendar />}
        {tab === 'tasks' && <TasksHub />}
        {tab === 'events' && <EventsTab />}
        {tab === 'calls' && <CallsTab />}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// EventsTab — list of M365 calendar events pulled from the unified endpoint
// with kind=event. Default window: next 30 days. Groups by day.
// -----------------------------------------------------------------------------
function EventsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ start: startOfDay(new Date()), end: addDays(startOfDay(new Date()), 30) });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/calendar/unified`, {
        params: {
          start: range.start.toISOString(),
          end: range.end.toISOString(),
          types: 'event',
          include_team: false,
        },
      });
      setItems(resp.data?.items || []);
    } catch (e) {
      console.error('events fetch failed', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const grouped = useMemo(() => {
    const m = {};
    for (const it of items) {
      if (!it.start_at) continue;
      try {
        const k = format(parseISO(it.start_at), 'yyyy-MM-dd');
        (m[k] = m[k] || []).push(it);
      } catch (_) { /* ignore */ }
    }
    return m;
  }, [items]);

  const rangePresets = [
    { label: 'Next 7 days', days: 7 },
    { label: 'Next 30 days', days: 30 },
    { label: 'Next 90 days', days: 90 },
  ];

  return (
    <div className="p-4 sm:p-6 h-full overflow-y-auto" data-testid="events-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Upcoming Events</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Meetings synced from your Microsoft 365 calendar.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden" data-testid="events-range">
          {rangePresets.map((p) => {
            const active = Math.round((range.end - range.start) / 86400000) === p.days;
            return (
              <button
                key={p.days}
                onClick={() => setRange({ start: startOfDay(new Date()), end: addDays(startOfDay(new Date()), p.days) })}
                className={`px-3 py-1.5 text-xs font-medium ${active ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                data-testid={`events-range-${p.days}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <p className="text-sm text-slate-400">Loading…</p>}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-300 dark:border-slate-600 rounded-xl">
          <Video className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No Microsoft 365 events in this window.</p>
          <p className="text-xs text-slate-400 mt-1">Connect your M365 account in Settings to sync calendar events.</p>
        </div>
      )}

      <div className="space-y-6">
        {Object.keys(grouped).sort().map((dayKey) => {
          const d = parseISO(dayKey);
          return (
            <section key={dayKey}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                {format(d, 'EEEE, MMM d')}
              </h3>
              <div className="space-y-2">
                {grouped[dayKey].map((ev) => <EventRow key={ev.id} event={ev} />)}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function EventRow({ event }) {
  const timeStr = (() => {
    try {
      const s = parseISO(event.start_at);
      const e = event.end_at ? parseISO(event.end_at) : null;
      return e ? `${format(s, 'p')} – ${format(e, 'p')}` : format(s, 'p');
    } catch (_) { return ''; }
  })();
  const meta = event.meta || {};
  return (
    <div
      className="flex items-start gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
      data-testid={`event-row-${event.id}`}
    >
      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
        <Video className="w-5 h-5 text-blue-600 dark:text-blue-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium text-sm text-slate-800 dark:text-white truncate">{event.title}</p>
          <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">{timeStr}</span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
          {meta.location && <span>📍 {meta.location}</span>}
          {meta.is_online && <span className="text-emerald-600">🎥 Online meeting</span>}
          {Array.isArray(meta.attendees) && meta.attendees.length > 0 && (
            <span>{meta.attendees.length} attendee{meta.attendees.length === 1 ? '' : 's'}</span>
          )}
          {event.owner_name && <span>· {event.owner_name}</span>}
        </div>
      </div>
    </div>
  );
}
